// Versioned registry of InteractAds-maintained components and behaviors
// (CREATIVE-MODEL-V1.md §6, INTERACTION-MODEL-V1.md §7 and §9, ADR-0018 §2.3).
//
// - Primitive element types live in the canonical schema (schema/document.ts).
// - Composite components (`ia.<name>@<major>`) and game behaviors live HERE:
//   each entry declares its props schema, slots, triggers it exposes,
//   commands it accepts, standard events it emits and the capabilities it
//   contributes to the capability profile (input of the billable product
//   classification, which InteractAds decides at publication — never here).
// - A published `@major` never changes semantics; an incompatible change is
//   a new major. Only the `ia.` namespace exists: there is no third-party
//   executable component in V1 (third-party sandbox: designed, disabled).
// - Runtime implementations are NOT part of 7.6B; this is the contract the
//   runtime, editor and AI catalog will share.

import type { EventType } from "@interactads/domain";
import { COLOR_PATTERN, type ElementType } from "./schema/document.js";
import {
  boolean,
  id,
  integer,
  number,
  object,
  oneOf,
  optional,
  string,
  type Infer,
  type Schema,
} from "./schema/dsl.js";

/** Document entities a component prop may reference (checked by the reference layer). */
export type RegistryRefKind = "survey" | "timer" | "variable";

export interface ComponentDefinition<P = unknown> {
  readonly type: string;
  readonly version: number;
  readonly description: string;
  readonly props: Schema<P>;
  /** Prop names whose value is a document id of the given kind. */
  readonly refs: Readonly<Record<string, RegistryRefKind>>;
  readonly slots: Readonly<
    Record<string, { readonly min: number; readonly max: number; readonly accepts: readonly ElementType[] }>
  >;
  readonly triggers: readonly string[];
  readonly commands: readonly string[];
  readonly emits: readonly EventType[];
  readonly capabilities: readonly string[];
}

export interface BehaviorDefinition {
  readonly type: string;
  readonly version: number;
  readonly description: string;
  readonly appliesTo: readonly ElementType[];
  readonly triggers: readonly string[];
  readonly capabilities: readonly string[];
}

// Optional colors (7.6E, additive within @1: absent = the runtime's documented default).
const color = string({ min: 1, max: 9, pattern: COLOR_PATTERN, patternName: "color #RRGGBB[AA]" });

/** Carousel auto-advance below this interval is reported (WCAG 2.2.2 guidance; CAROUSEL_AUTOPLAY_TOO_FAST). */
export const CAROUSEL_MIN_COMFORTABLE_INTERVAL_MS = 2_000;

const carouselProps = object({
  /** 0 or absent = no auto-advance. Auto-advance stops for good after the first user navigation. */
  autoAdvanceMs: optional(integer({ min: 0, max: 60_000 })),
  loop: boolean(),
  gesture: oneOf(["swipe", "none"]),
  indicators: oneOf(["dots", "none"]),
  transition: oneOf(["slide", "fade"]),
  /** Arrows/dots/pause control color. */
  accentColor: optional(color),
});
const surveyQuestionProps = object({
  survey: id(),
  optionLayout: oneOf(["stack", "grid"]),
  optionStyle: oneOf(["pill", "card", "plain"]),
  autoAdvance: boolean(),
  textColor: optional(color),
  accentColor: optional(color),
});
const progressProps = object({
  source: oneOf(["survey", "timer", "variable"]),
  style: oneOf(["bar", "dots", "ring"]),
  survey: optional(id()),
  timer: optional(id()),
  variable: optional(id()),
  max: optional(integer({ min: 1, max: 1_000_000 })),
  color: optional(color),
});
const countdownProps = object({
  timer: id(),
  format: oneOf(["ss", "mm:ss"]),
  showZero: optional(boolean()),
  /** Look of the runtime-drawn digits (7.6F, additive; absent = runtime default #111827 / 60 % of the height). */
  color: optional(color),
  fontSize: optional(number({ min: 8, max: 200, decimals: 2 })),
});

export type CarouselProps = Infer<typeof carouselProps>;
export type SurveyQuestionProps = Infer<typeof surveyQuestionProps>;
export type ProgressProps = Infer<typeof progressProps>;
export type CountdownProps = Infer<typeof countdownProps>;

const def = <P>(d: ComponentDefinition<P>): ComponentDefinition<P> => d;

export const COMPONENT_REGISTRY_V1 = {
  "ia.carousel@1": def({
    type: "ia.carousel",
    version: 1,
    description:
      "Slider of slides; each slide is a group of primitive elements. Swipe/arrows/dots, optional auto-advance.",
    props: carouselProps,
    refs: {},
    slots: { slides: { min: 2, max: 12, accepts: ["group"] } },
    triggers: ["slideChanged"],
    commands: ["next", "prev", "goTo"],
    emits: ["carousel_slide_view", "carousel_navigate"],
    capabilities: ["carousel"],
  }),
  "ia.survey-question@1": def({
    type: "ia.survey-question",
    version: 1,
    description:
      "Renders the current question of the survey instrument with its closed options and advances along the graph.",
    props: surveyQuestionProps,
    refs: { survey: "survey" },
    slots: {},
    triggers: [],
    commands: [],
    emits: ["survey_question_view", "answer"],
    capabilities: ["survey"],
  }),
  "ia.progress@1": def({
    type: "ia.progress",
    version: 1,
    description: "Progress bar/dots/ring bound to the survey path, a timer or a numeric variable.",
    props: progressProps,
    refs: { survey: "survey", timer: "timer", variable: "variable" },
    slots: {},
    triggers: [],
    commands: [],
    emits: [],
    capabilities: ["progress"],
  }),
  "ia.countdown@1": def({
    type: "ia.countdown",
    version: 1,
    description: "Visual countdown of a declared timer.",
    props: countdownProps,
    refs: { timer: "timer" },
    slots: {},
    triggers: [],
    commands: [],
    emits: [],
    capabilities: ["countdown"],
  }),
} as const satisfies Readonly<Record<string, ComponentDefinition>>;

export type ComponentRef = keyof typeof COMPONENT_REGISTRY_V1;

const VISUAL: readonly ElementType[] = ["text", "image", "shape", "button", "group", "hotspot", "component"];

/** Game behaviors (version 1 is the one defined by schemaVersion 2). */
export const BEHAVIOR_REGISTRY_V1 = {
  draggable: {
    type: "draggable",
    version: 1,
    description: "Pointer drag within bounds; optional drop targets.",
    appliesTo: VISUAL,
    triggers: ["pointer.drag.start", "pointer.drag.end", "drag.drop"],
    capabilities: ["game", "drag"],
  },
  move: {
    type: "move",
    version: 1,
    description: "Constant velocity or path movement with fixed-step integration.",
    appliesTo: VISUAL,
    triggers: [],
    capabilities: ["game"],
  },
  collider: {
    type: "collider",
    version: 1,
    description: "Rect/ellipse collision volume for collision.enter/exit triggers.",
    appliesTo: VISUAL,
    triggers: ["collision.enter", "collision.exit"],
    capabilities: ["game"],
  },
  "follow-pointer": {
    type: "follow-pointer",
    version: 1,
    description: "Follows the pointer along an axis with optional smoothing.",
    appliesTo: VISUAL,
    triggers: [],
    capabilities: ["game"],
  },
  "tappable-target": {
    type: "tappable-target",
    version: 1,
    description: "Hit feedback and enlarged hit area for tap games.",
    appliesTo: VISUAL,
    triggers: [],
    capabilities: ["game"],
  },
} as const satisfies Readonly<Record<string, BehaviorDefinition>>;

export type ResolvedComponent =
  | { readonly ok: true; readonly ref: ComponentRef; readonly definition: ComponentDefinition }
  | { readonly ok: false; readonly reason: "UNKNOWN_COMPONENT" | "UNKNOWN_VERSION" };

/** Exact resolution by "ia.<name>@<major>"; never falls back to another version. */
export function resolveComponent(ref: string): ResolvedComponent {
  if (Object.hasOwn(COMPONENT_REGISTRY_V1, ref))
    return {
      ok: true,
      ref: ref as ComponentRef,
      definition: COMPONENT_REGISTRY_V1[ref as ComponentRef],
    };
  const name = ref.split("@")[0];
  const known = Object.values(COMPONENT_REGISTRY_V1).some((d) => d.type === name);
  return { ok: false, reason: known ? "UNKNOWN_VERSION" : "UNKNOWN_COMPONENT" };
}

/** Registry descriptor for catalogs (editor palette, AI catalog). Props schemas are omitted (not serializable). */
export function describeRegistry(): {
  readonly components: ReadonlyArray<Omit<ComponentDefinition, "props"> & { readonly ref: string }>;
  readonly behaviors: readonly BehaviorDefinition[];
} {
  return {
    components: Object.entries(COMPONENT_REGISTRY_V1)
      .map(([ref, d]) => ({
        ref,
        type: d.type,
        version: d.version,
        description: d.description,
        refs: d.refs,
        slots: d.slots,
        triggers: d.triggers,
        commands: d.commands,
        emits: d.emits,
        capabilities: d.capabilities,
      }))
      .sort((a, b) => (a.ref < b.ref ? -1 : 1)),
    behaviors: Object.values(BEHAVIOR_REGISTRY_V1),
  };
}
